import styled from "@emotion/styled";
import { Typography } from "@mui/material";
import Checkbox, { type CheckboxProps } from "@mui/material/Checkbox";
import { CheckboxCheckedIcon } from "Components/_Shared/Icons/CheckboxChecked";
import { type FieldErrorProps, useFieldError } from "Forms/Hooks/useFieldError";
import { Radius, Spacing } from "Infrastructure/Themes/Themes";
import type * as React from "react";

type Props = CheckboxProps &
  FieldErrorProps & {
    label?: string | React.JSX.Element;
    isLabelFirst?: boolean;
    hasFilterStyle?: boolean;
    hasErrorMessage?: boolean;
  };

const StyledWrapper = styled.div<{
  $hasFilterStyle: boolean;
  $disabled: boolean;
}>`
  display: flex;
  user-select: none;
  align-items: center;

  & > label {
    display: flex;
    cursor: pointer;
    padding-left: ${(props) => props.theme.spacing(1)};
    width: 100%;
    color: ${(props) =>
      props.$hasFilterStyle
        ? props.theme.palette.primary.main
        : props.$disabled
          ? props.theme.palette.text.disabled
          : props.theme.palette.text.primary};
  }
`;

const StyledCheckbox = styled(Checkbox)`
  padding: 0;
  color: ${(props) => props.theme.colors.borderLight};
  border-radius: ${(props) => props.theme.spacing(Radius.S)};

  &.Mui-checked + label {
    .MuiTypography-root {
      font-weight: 600;
    }
  }
`;

const StyledError = styled.div<{ $hasErrorMessage?: boolean }>`
  min-height: ${(props) => (props.$hasErrorMessage ? "20px" : 0)};
`;

const ErrorTypography = styled(Typography)`
  color: ${(props) => props.theme.colors.validation};
  font-size: 0.75rem;
  padding-left: ${(props) => props.theme.spacing(3.4)};
`;

export const BlCheckboxInput: React.FunctionComponent<Props> = (props) => {
  const { hasError, errorMessage, originalProps } = useFieldError(props);
  const { label, isLabelFirst, hasFilterStyle, hasErrorMessage, onChange, ...rest } = originalProps;

  const LabelElement = (
    <label htmlFor={props.name}>
      {typeof label === "string" ? (
        <Typography fontSize={hasFilterStyle ? 12 : 16} fontWeight={hasFilterStyle ? 600 : 400}>
          {label}
        </Typography>
      ) : (
        label
      )}
    </label>
  );
  return (
    <div>
      <StyledWrapper className={"checkbox"} $hasFilterStyle={hasFilterStyle ?? false} $disabled={rest.disabled ?? false}>
        {isLabelFirst && label && LabelElement}
        <StyledCheckbox id={props.name} onChange={onChange} {...rest} />
        {!isLabelFirst && label && LabelElement}
      </StyledWrapper>
      <StyledError $hasErrorMessage={hasErrorMessage}>
        <ErrorTypography>{hasError ? errorMessage : ""}</ErrorTypography>
      </StyledError>
    </div>
  );
};
